import { apiService } from './apiService';
import { ChatMessage, ProposalState, ProposalStatus } from '../models/clinicAgent';
import { API_BASE_URL } from '../config/env';

// Raw row shape from GET /api/chat/ (ChatSerializer).
interface ChatHistoryRow {
  id: number;
  message: string;
  role: 'user' | 'assistant';
  createdAt: string; // "dd-mm-yyyy HH:MM:SS"
  read: boolean;
  // Attachment: a uri-compatible string (hosted URL for history, or a base64
  // data URL) plus its MIME type. Images render as <img>; PDFs as a file chip.
  // fileName is present for PDFs (read snake_case too, in case the serializer
  // exposes it that way like message_type).
  fileData?: string;
  fileType?: string;
  fileName?: string;
  file_name?: string;
  // "text" (default) or "proposal" (a persisted proposal whose state lives in
  // metadata). Read both casings: existing fields arrive camelCase, but the
  // serializer may expose snake_case.
  messageType?: 'text' | 'proposal';
  message_type?: 'text' | 'proposal';
  trigger?: string;
  // For a proposal row: { proposal_id, run_id, action, patient_id, payload,
  // rationale, status, detail }.
  metadata?: Record<string, unknown>;
}

// Map a persisted proposal row's metadata onto a renderable card. Server status
// is pending | applied | discarded; the transient 'applying'/'error' states are
// local only, so anything unrecognised falls back to 'pending' (still openable).
function toProposalState(md: Record<string, unknown>): ProposalState {
  const raw = String(md.status ?? 'pending').toLowerCase();
  const status: ProposalStatus =
    raw === 'applied' ? 'applied' : raw === 'discarded' ? 'discarded' : 'pending';
  const runId = Number(md.run_id);
  return {
    id: typeof md.proposal_id === 'string' ? md.proposal_id : undefined,
    run_id: Number.isFinite(runId) ? runId : undefined,
    action: String(md.action ?? ''),
    patient_id: (md.patient_id as number | null | undefined) ?? null,
    payload: (md.payload as Record<string, unknown>) ?? {},
    rationale: String(md.rationale ?? ''),
    detail: (md.detail as Record<string, unknown>) ?? undefined,
    status,
  };
}

// Parse the backend's "dd-mm-yyyy HH:MM:SS" timestamp into epoch ms.
function parseTs(raw: string): number {
  const m = /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/.exec(raw || '');
  if (!m) return Date.now();
  const [, dd, mm, yyyy, hh, min, ss] = m;
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(ss)).getTime();
}

// A page of transcript plus whether older pages remain (DRF `next` cursor).
export interface ChatHistoryPage {
  messages: ChatMessage[];
  hasMore: boolean;
}

export const clinicChatHistoryService = {
  // Load one page of the clinician's own chat transcript. GET only — never POST
  // /api/chat/ from the clinic client (that runs the patient coach persona).
  // Rows arrive newest-first; we return them chronological for rendering, so
  // page 1 is the most recent slice and higher pages are progressively older.
  getHistory: async (page: number = 1): Promise<ChatHistoryPage> => {
    const data = await apiService.get(`${API_BASE_URL}/chat/?page=${page}`);
    const rows: ChatHistoryRow[] = data?.results ?? data ?? [];
    // This API paginates with { actualPage, totalPages, perpage } (not DRF's
    // next/previous). Prefer page counters; fall back to a full-page heuristic
    // or a DRF `next` cursor so this keeps working if the backend shape changes.
    const totalPages = Number(data?.totalPages);
    const actualPage = Number(data?.actualPage ?? page);
    const perpage = Number(data?.perpage);
    const hasMore = Number.isFinite(totalPages) && totalPages > 0
      ? actualPage < totalPages
      : data?.next != null
        ? Boolean(data.next)
        : Number.isFinite(perpage) && perpage > 0
          ? rows.length >= perpage
          : false;
    const messages: ChatMessage[] = rows.map((r) => {
      const messageType = r.messageType ?? r.message_type ?? 'text';
      const base: ChatMessage = {
        id: `hist-${r.id}`,
        role: r.role,
        text: r.message ?? '',
        ts: parseTs(r.createdAt),
        ...(r.fileData
          ? {
              fileUrl: r.fileData,
              fileType: r.fileType,
              ...(r.fileName ?? r.file_name ? { fileName: r.fileName ?? r.file_name } : {}),
            }
          : {}),
      };

      // A persisted proposal row: empty message, state lives in metadata. Render
      // as an interactive card (its status drives whether the buttons show).
      if (messageType === 'proposal' && r.metadata) {
        return {
          ...base,
          messageType: 'proposal',
          proposals: [toProposalState(r.metadata)],
        };
      }

      return base;
    });
    // API is newest-first; reverse to chronological.
    return { messages: messages.reverse(), hasMore };
  },
};
